import { BreadcrumbBox } from '@/features/Breadcrumb';
import { FeaturedContent } from '@/features/FeaturedContent';
import { SEO } from '@/features/SEO';
import Button from '@/features/shared/Button';
import ContentContainer from '@/features/shared/ContentContainer';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { MuiContainer } from '@/features/shared/material-ui';
import SocialShare from '@/features/shared/SocialShare';
import { ColorNames, LoadingStatusValues, SpacingSizes, Theme } from '@/utils/constants';
import { getDocumentBasedImage } from '@/utils/helpers';
import { resolveTheme } from '@/utils/resolvers';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import clsx from 'clsx';
import React, { memo } from 'react';
import { BlogDetailProps } from './BlogDetail.interface';
import BlogDetailSkeleton from './BlogDetail.skeleton';
import useStyles from './BlogDetail.style';
import { HeaderContent } from './HeaderContent';
import { MainContent } from './MainContent';
import { Author } from './shared';

const resolveBackLinkBackground = ({
  hasAuthor,
  hasRelateContent,
  bgBlogDetail,
  bgRelatedContent,
  bgBlack,
}: any) => {
  if (hasRelateContent) {
    return bgRelatedContent;
  }

  if (hasAuthor) {
    return bgBlack;
  }

  return bgBlogDetail;
};

const BlogDetail = ({
  author,
  backgroundColor,
  contentButtons,
  detailContent,
  displaySocialShare,
  legacyContent,
  pageMetadata,
  publicationDate,
  relatedContent,
  relatedContentLink,
  shortDescription,
  tags,
  contentCategory,
  podcastSeries,
  keys,
  theme = Theme.light,
  title,
  labels,
  cardColor,
  backLink,
  cardImage = {},
}: Br.PropsWithContentButtons<BlogDetailProps>) => {
  const classes = useStyles({});
  const hasAuthor = Boolean(author?.name);
  const hasRelateContent = Boolean(relatedContent?.length);
  const bgRelatedContent = ColorNames.darkGray;
  const bgBlack = ColorNames.black;

  const bgBackLink = resolveBackLinkBackground({
    hasAuthor,
    hasRelateContent,
    bgBlogDetail: backgroundColor,
    bgRelatedContent,
    bgBlack,
  });
  const bgBackLinkTheme = resolveTheme('bg-color')({ rawVal: bgBackLink });

  const headerContentProps = {
    title,
    publicationDate: publicationDate?.formatted,
    shortDescription,
    author,
    tags,
    theme,
    backgroundColor,
  };

  const mainContentProps = {
    legacyContent,
    detailContent,
    theme,
  };

  const relatedContentProps = {
    theme: Theme.dark,
    template: 'grid',
    curatedList: {
      listTitle: labels?.related_content || 'Related Content',
      contentList: relatedContent,
      cta: relatedContentLink,
    },
    isCardImages: true,
    presentation: {
      topSpacing: SpacingSizes.small,
      bottomSpacing: SpacingSizes.medium,
      topBackgroundColor: bgRelatedContent,
      bottomBackgroundColor: bgRelatedContent,
      cardVerticalAlignmentNoImage: 'flex-start',
    },
    cardTheme: theme,
    showCaption: false,
    cardColor,
  };

  const metadata = {
    ...pageMetadata,
    metaTitle: pageMetadata?.metaTitle || title,
    description: pageMetadata?.description || shortDescription,
    socialMediaImage: getDocumentBasedImage({ pageMetadata, cardImage }),
  };

  const rootClassName = clsx(classes.root, `${theme}-theme`);

  const { status } = React.useContext(ElasticComponentContext);

  return (
    <article className={rootClassName}>
      <ContentContainer
        topSpacing={SpacingSizes.small}
        bottomSpacing={hasAuthor ? SpacingSizes.small : SpacingSizes.medium}
        topBackgroundColor={backgroundColor}
        bottomBackgroundColor={backgroundColor}
      >
        <div className={classes.container}>
          <MuiContainer>
            <BreadcrumbBox className={classes.breadcrumb} theme={bgBackLinkTheme} />
          </MuiContainer>
          <MuiContainer className="small">
            <header data-datalayer-position="header_content">
              {contentButtons?.content}
              <HeaderContent {...headerContentProps} />
            </header>
            <section>
              <MainContent {...mainContentProps} />
              {displaySocialShare && (
                <SocialShare
                  shareText={labels?.share_text || 'Share'}
                  theme={theme}
                  title={pageMetadata?.metaTitle || title}
                />
              )}
            </section>
          </MuiContainer>
        </div>
      </ContentContainer>

      <footer className={classes.footer}>
        {hasAuthor && (
          <ContentContainer
            topSpacing={SpacingSizes.small}
            bottomSpacing={SpacingSizes.small}
            topBackgroundColor={backgroundColor}
            bottomBackgroundColor={hasRelateContent ? bgRelatedContent : bgBlack}
          >
            <MuiContainer className="small">
              <Author theme={Theme.dark} variant="detail" author={author} />
            </MuiContainer>
          </ContentContainer>
        )}

        {backLink && (
          <ContentContainer
            topSpacing={hasAuthor ? SpacingSizes.none : SpacingSizes.small}
            bottomSpacing={hasRelateContent ? SpacingSizes.none : SpacingSizes.small}
            topBackgroundColor={bgBackLink}
            bottomBackgroundColor={bgBackLink}
          >
            <MuiContainer className="small">
              <Button
                className={classes.backLink}
                link={backLink}
                variant="link"
                startIcon={<ChevronLeftIcon color="inherit" fontSize="small" />}
                theme={bgBackLinkTheme}
              />
            </MuiContainer>
          </ContentContainer>
        )}

        {hasRelateContent && status !== LoadingStatusValues.loading && (
          <section>
            <FeaturedContent {...relatedContentProps} />
          </section>
        )}
        {hasRelateContent && status === LoadingStatusValues.loading && (
          <BlogDetailSkeleton theme={theme} />
        )}
      </footer>
      <SEO
        metadata={metadata}
        tags={keys}
        contentCategory={contentCategory}
        publicationDate={publicationDate?.iso}
        podcastSeries={podcastSeries}
        isDocumentBackedPage
      />
    </article>
  );
};

export default memo(BlogDetail);
